import { Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { concatMap, debounceTime, filter, map } from 'rxjs';

import { RewardTokenType } from '../../../../lib/tokens-base/token-base.models';
import { selectIsLinearBetslip } from '../../../base/store/selectors';
import { IBetslipRootState } from '../../../base/store/state';
import { BetslipType } from '../../../core/betslip-type';
import BetslipComboBetActions from '../../combo-bet/actions';
import BetslipPickActions from '../../picks/actions';
import BetslipSingleBetActions from '../../single-bet/actions';
import { singleBetPicksSelector } from '../../single-bet/selectors';
import { BetslipTeaserActions } from '../../teaser-bet/actions';
import { BetslipTypesActions } from '../../types/actions';
import { betslipTypeStateSelector } from '../../types/selectors';
import { RewardTokensActions } from '../actions';
import { selectAllAssignedTokens, selectEligibilityStateFactory, selectRecoverableTokens, selectRewardTokens } from '../selectors';
import { getTokenSelectionContext, isTokenRecoverable } from '../services/linear-reward-tokens.utils';
import { ITokenStoreState } from '../state';

@Injectable()
export class RewardTokenRecoveryEffects {
    constructor(
        private actions$: Actions,
        private store: Store<IBetslipRootState>,
    ) {}

    reselectTokensOnTabChangeEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipTypesActions.setSelectedType),
            concatLatestFrom(() => [this.store.select(selectIsLinearBetslip), this.store.select(selectRecoverableTokens)]),
            filter(([action, isLinear, recoverableTokens]) => !isLinear && action.betslipType !== null && recoverableTokens.length > 0),
            concatMap(([action, , recoverableTokens]) => {
                const recoverableTokensForType = recoverableTokens.filter((x) => x.context.betslipType === action.betslipType);

                return recoverableTokensForType.map((recoveryState) => RewardTokensActions.recoverToken(recoveryState));
            }),
        );
    });

    recoverTokensSingleEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(RewardTokensActions.recoverToken),
            filter((action) => action.context.betslipType === BetslipType.Single && !!action.context.pickId),
            concatLatestFrom((action) => [
                this.store.select(singleBetPicksSelector),
                this.store.select(selectRewardTokens),
                this.store.select(selectEligibilityStateFactory(action.context)),
            ]),
            filter(
                ([action, picks, tokens, eligbility]) =>
                    !!picks[action.context.pickId!.toString()] && isTokenRecoverable(action.tokenId, tokens, eligbility),
            ),
            map(([action]) =>
                BetslipSingleBetActions.setSingleBetPickRewardToken({
                    pickId: action.context.pickId!.toString(),
                    rewardTokenId: action.tokenId,
                }),
            ),
        );
    });

    recoverTokensComboEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(RewardTokensActions.recoverToken),
            filter((action) => action.context.betslipType === BetslipType.Combo),
            concatLatestFrom((action) => [this.store.select(selectRewardTokens), this.store.select(selectEligibilityStateFactory(action.context))]),
            filter(([action, tokens, eligbility]) => isTokenRecoverable(action.tokenId, tokens, eligbility)),
            map(([action]) => BetslipComboBetActions.setComboBetRewardToken({ rewardTokenId: action.tokenId })),
        );
    });

    recoverTokensTeaserEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(RewardTokensActions.recoverToken),
            filter((action) => action.context.betslipType === BetslipType.Teaser),
            concatLatestFrom((action) => [this.store.select(selectRewardTokens), this.store.select(selectEligibilityStateFactory(action.context))]),
            filter(([action, tokens, eligbility]) => isTokenRecoverable(action.tokenId, tokens, eligbility)),
            map(([action]) => BetslipTeaserActions.setTeaserBetRewardToken({ rewardTokenId: action.tokenId })),
        );
    });

    removeRecoverableTokenOnTabRecoveryEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(RewardTokensActions.recoverToken),
            map((action) => RewardTokensActions.removeRecoverableTokenForContext(action.context)),
        );
    });

    removeRecoverableTokensOnTabChangeEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipTypesActions.setSelectedType),
            concatLatestFrom(() => [this.store.select(selectIsLinearBetslip), this.store.select(selectRecoverableTokens)]),
            filter(
                ([action, isLinear, recoverableTokens]) =>
                    !isLinear && (action.betslipType === null || action.betslipType === BetslipType.EditBet) && recoverableTokens.length > 0,
            ),
            map(() => RewardTokensActions.clearRecoverableTokens()),
        );
    });

    removeRecoverableTokensOnPickRemove$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipPickActions.removePick),
            concatLatestFrom(() => [this.store.select(selectIsLinearBetslip), this.store.select(selectRecoverableTokens)]),
            filter(([, isLinear, recoverableTokens]) => !isLinear && recoverableTokens.length > 0),
            map(([action]) =>
                RewardTokensActions.removeRecoverableTokenForContext({
                    pickId: action.pickId,
                    betslipType: BetslipType.Single,
                }),
            ),
        );
    });

    removeRecoverableTokensOnPicksRemoval$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipPickActions.removeMultiplePicks),
            concatLatestFrom(() => [this.store.select(selectIsLinearBetslip), this.store.select(selectRecoverableTokens)]),
            filter(([, isLinear, recoverableTokens]) => !isLinear && recoverableTokens.length > 0),
            concatMap(([action]) =>
                action.pickIds.map((pickId) =>
                    RewardTokensActions.removeRecoverableTokenForContext({
                        pickId,
                        betslipType: BetslipType.Single,
                    }),
                ),
            ),
        );
    });

    deselectTokensOnTabChangeEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipTypesActions.setSelectedType),
            concatLatestFrom(() => [
                this.store.select(selectIsLinearBetslip),
                this.store.select(selectAllAssignedTokens),
                this.store.select(betslipTypeStateSelector),
            ]),
            filter(
                ([action, isLinear, _]) =>
                    !isLinear && action.betslipType !== null && (!action.previousType || action.previousType !== action.betslipType),
            ),
            debounceTime(1),
            map(([, , assignedTokens, types]) => {
                const tokens = assignedTokens
                    .map((t) => ({
                        tokenId: t.userTokenId,
                        context: getTokenSelectionContext(t.userTokenId, types),
                        isRecoverable: t.rewardTokenType !== RewardTokenType.AccaBoost,
                    }))
                    .filter((storeState): storeState is ITokenStoreState => !!storeState.context);

                return RewardTokensActions.deselectTokensFromTabSwitch({ payload: tokens });
            }),
        );
    });

    deselectTokensFromTabSwitchEffect = createEffect(() => {
        return this.actions$.pipe(
            ofType(RewardTokensActions.deselectTokensFromTabSwitch),
            concatMap((tokens) => tokens.payload.map((t) => RewardTokensActions.deselectTokenForContext({ payload: t.context }))),
        );
    });

    storeTokensFromTabSwitchEffect = createEffect(() => {
        return this.actions$.pipe(
            ofType(RewardTokensActions.deselectTokensFromTabSwitch),
            filter((action) => action.payload.length > 0),
            map((action) => {
                const storeStates = action.payload;

                const recoveryStates = storeStates
                    .filter((state) => state.isRecoverable)
                    .map((state) => ({
                        tokenId: state.tokenId,
                        context: state.context,
                    }));

                return RewardTokensActions.storeRecoverableTokens({ payload: recoveryStates });
            }),
        );
    });
}
